import React, { useEffect, useState } from "react"
import { useMutation } from "urql"

import { Input, Toggle } from "../../../../components/shared/Inputs"
import { Box } from "../../../../components/shared/Layout"
import Typography from "../../../../components/shared/Typography"
import { useToast } from "../../../../contexts/ToastContext"
import { isHomeFullImageHero } from "../../utils/reviewHeroUtils"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import ImagePickerV2 from "../shared/ImagePickerV2"

import EditorFooter from "./shared/EditorFooter"

const UPLOAD_IMAGE_MUTATION = `
  mutation UploadImage($handle: String!, $description: String!, $provider: String) {
    uploadImage(handle: $handle, description: $description, provider: $provider) {
      result
      errors
      photoUrl
      altText
    }
  }
`

const ReviewHeroEditor = ({ section, practice, onSave, onCancel, onDelete, isEditing = false }) => {
  const [errors, setErrors] = useState({})
  const { handleUpdateSectionPreview, clearPreview, pages } = useWebsiteBuilderV2()
  const { showToast } = useToast()
  const [{ fetching: uploadFetching }, uploadImage] = useMutation(UPLOAD_IMAGE_MUTATION)

  const homeHasFullImage = isHomeFullImageHero(pages)

  const [formData, setFormData] = useState({
    headline: section?.headline || "What clients are saying",
    imageUrl: section?.imageUrl || "",
    settings: {
      showRatingSummary: section?.settings?.showRatingSummary ?? true,
      useThemeBackground: section?.settings?.useThemeBackground ?? true,
      useHomeHeroImage: section?.settings?.useHomeHeroImage ?? true,
      backgroundColor: section?.settings?.backgroundColor || "#FFFFFF",
      ...section?.settings
    }
  })

  useEffect(() => {
    if (section) {
      setFormData({
        headline: section.headline || "What clients are saying",
        imageUrl: section.imageUrl || "",
        settings: {
          showRatingSummary: section?.settings?.showRatingSummary ?? true,
          useThemeBackground: section?.settings?.useThemeBackground ?? true,
          useHomeHeroImage: section?.settings?.useHomeHeroImage ?? true,
          backgroundColor: section?.settings?.backgroundColor || "#FFFFFF",
          ...section?.settings
        }
      })
    }
  }, [section])

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value }
      if (section?.id && handleUpdateSectionPreview) {
        handleUpdateSectionPreview(section.id, {
          ...section,
          layout: "review_hero",
          headline: next.headline,
          imageUrl: next.imageUrl,
          settings: { ...section.settings, ...next.settings }
        })
      }
      return next
    })
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }))
  }

  const handleSettingsChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, settings: { ...prev.settings, [field]: value } }
      if (section?.id && handleUpdateSectionPreview) {
        handleUpdateSectionPreview(section.id, {
          ...section,
          layout: "review_hero",
          headline: next.headline,
          imageUrl: next.imageUrl,
          settings: { ...section.settings, ...next.settings }
        })
      }
      return next
    })
  }

  const handleImageSelect = ({ handle, provider }) => {
    uploadImage({
      handle,
      description: "review_hero_image",
      provider
    }).then((result) => {
      if (result?.data?.uploadImage?.result === "success") {
        const photoUrl = result.data.uploadImage.photoUrl
        handleChange("imageUrl", photoUrl)
        showToast("Image uploaded successfully!")
      } else {
        let errorMessage = "There was an error uploading your image. Please try again later or contact support."
        if (result?.data?.uploadImage?.errors) errorMessage += ` ${result.data.uploadImage.errors}`
        showToast({ type: "error", content: errorMessage })
      }
    })
  }

  const handleImageRemove = () => {
    handleChange("imageUrl", "")
    showToast("Image removed.")
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.headline.trim()) {
      newErrors.headline = "Headline is required"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e?.preventDefault?.()
    if (validateForm()) {
      onSave({
        layout: "review_hero",
        headline: formData.headline,
        imageUrl: formData.imageUrl || null,
        settings: {
          showRatingSummary: formData.settings.showRatingSummary,
          useThemeBackground: formData.settings.useThemeBackground,
          useHomeHeroImage: formData.settings.useHomeHeroImage,
          backgroundColor: formData.settings.backgroundColor
        }
      })
    }
  }

  const handleCancelClick = () => {
    clearPreview?.()
    onCancel()
  }

  const hasCustomImage = Boolean(formData.imageUrl)

  return (
    <>
      <Box className="space-y-6">
        <div className="border-b border-gray-200 pb-4">
          <Typography variant="h4" className="text-xl font-semibold">
            {isEditing ? "Edit Reviews Hero" : "Add Reviews Hero"}
          </Typography>
          <Typography variant="body" className="mt-1 text-gray-600">
            Configure the hero that appears on the reviews page.
          </Typography>
        </div>

        <div className="space-y-4">
          <Input
            label="Headline"
            value={formData.headline}
            onChange={(e) => handleChange("headline", e.target.value)}
            placeholder="What clients are saying"
            error={errors.headline}
          />

          <div className="space-y-3 rounded-lg border border-gray-200 p-4">
            <div>
              <p className="text-sm font-semibold text-gray-800">Background image</p>
              <p className="text-sm text-gray-500">
                Upload a hero image for the reviews page, same as other builder pages.
              </p>
            </div>

            {hasCustomImage ? (
              <div className="flex items-center gap-4">
                <img
                  src={formData.imageUrl}
                  alt="Reviews hero background"
                  className="size-16 rounded-lg object-cover"
                />
                <div className="flex flex-col gap-2">
                  <ImagePickerV2
                    practice={practice}
                    onImageSelect={handleImageSelect}
                    fetching={uploadFetching}
                    showCurateTab={true}
                    currentImageUrl={formData.imageUrl}
                    trigger={
                      <button
                        type="button"
                        className="font-bold text-builder-accent hover:underline"
                        disabled={uploadFetching}>
                        Edit image
                      </button>
                    }
                  />
                  <button
                    type="button"
                    className="font-bold text-red-500 hover:underline"
                    onClick={handleImageRemove}
                    disabled={uploadFetching}>
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <>
                <ImagePickerV2
                  practice={practice}
                  onImageSelect={handleImageSelect}
                  fetching={uploadFetching}
                  showCurateTab={true}
                  currentImageUrl={formData.imageUrl}
                  trigger={
                    <div className="flex w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-gray-300 bg-white p-6 transition-colors hover:border-teal-500">
                      <div className="flex size-10 items-center justify-center rounded-full bg-gray-100">
                        <svg className="size-5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                      </div>
                      <p className="text-sm font-semibold text-teal-600">Upload an image</p>
                      <p className="text-xs text-gray-500">Supported formats: PNG, JPG, SVG</p>
                    </div>
                  }
                />

                {homeHasFullImage && practice?.headerHeroImageUrl && (
                  <div className="flex items-center justify-between border-t border-gray-200 pt-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Use home page hero image</p>
                      <p className="text-sm text-gray-500">
                        Fall back to the homepage full-image hero when no custom image is set.
                      </p>
                    </div>
                    <Toggle
                      checked={formData.settings.useHomeHeroImage}
                      onChange={(checked) => handleSettingsChange("useHomeHeroImage", checked)}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
            <div>
              <p className="text-sm font-semibold text-gray-800">Show rating summary</p>
              <p className="text-sm text-gray-500">Display average rating and total review count.</p>
            </div>
            <Toggle
              checked={formData.settings.showRatingSummary}
              onChange={(checked) => handleSettingsChange("showRatingSummary", checked)}
            />
          </div>

          <div className="space-y-3 rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-800">Use theme background</p>
                <p className="text-sm text-gray-500">
                  If off, set a custom background color when no hero image is shown.
                </p>
              </div>
              <Toggle
                checked={formData.settings.useThemeBackground}
                onChange={(checked) => handleSettingsChange("useThemeBackground", checked)}
              />
            </div>

            {!formData.settings.useThemeBackground && (
              <Input
                label="Background color"
                type="text"
                value={formData.settings.backgroundColor}
                onChange={(e) => handleSettingsChange("backgroundColor", e.target.value)}
                placeholder="#FFFFFF"
              />
            )}
          </div>
        </div>
      </Box>

      <EditorFooter onDelete={onDelete} onCancel={handleCancelClick} onSave={handleSubmit} showDelete={isEditing} />
    </>
  )
}

export default ReviewHeroEditor
